import { useCallback, useSyncExternalStore } from 'react';
import { pagesEn } from './pages.en';
import { componentsEn } from './components.en';
import { commonEn } from './common.en';
import {
  LANGUAGE_STORAGE_KEY, isLanguagePreference, resolveLanguage, translateMessage,
  type Language, type LanguagePreference, type TranslationParams,
} from './core';

export type { Language, LanguagePreference, TranslationParams } from './core';

const english: Record<string, string> = { ...pagesEn, ...componentsEn, ...commonEn };
const listeners = new Set<() => void>();
let preference: LanguagePreference = 'system';

try {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (isLanguagePreference(stored)) preference = stored;
} catch {
  // Language switching must still work when browser storage is unavailable.
}

export function getLanguage(): Language {
  const languages = typeof navigator === 'undefined' ? [] : [...navigator.languages, navigator.language];
  return resolveLanguage(preference, languages);
}

function syncDocument() {
  if (typeof document === 'undefined') return;
  const language = getLanguage();
  document.documentElement.lang = language;
  document.title = `OSSshelf - ${language === 'zh-CN' ? '文件管理系统' : 'File management'}`;
  document.querySelector('meta[name="description"]')?.setAttribute('content', language === 'zh-CN'
    ? 'OSSshelf - 基于 Cloudflare 部署的多厂商 OSS 文件管理系统，支持 WebDAV、预签名直传、安全分享'
    : 'OSSshelf - Multi-provider object storage management with WebDAV, presigned uploads and secure sharing');
}

function notify() {
  syncDocument();
  listeners.forEach((listener) => listener());
}

export function setPreference(next: LanguagePreference) {
  if (!isLanguagePreference(next)) return;
  preference = next;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  } catch {
    // Preserve the in-memory choice rather than forcing a page reload.
  }
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot() {
  return `${preference}:${getLanguage()}`;
}

if (typeof window !== 'undefined') {
  window.addEventListener('languagechange', () => { if (preference === 'system') notify(); });
  window.addEventListener('storage', (event) => {
    if (event.key !== LANGUAGE_STORAGE_KEY && event.key !== null) return;
    preference = isLanguagePreference(event.newValue) ? event.newValue : 'system';
    notify();
  });
}
syncDocument();

export function translate(message: string, params?: TranslationParams): string {
  return translateMessage(getLanguage(), english, message, params);
}

export function useI18n() {
  useSyncExternalStore(subscribe, getSnapshot, () => 'system:en-US');
  const language = getLanguage();
  const t = useCallback((message: string, params?: TranslationParams) =>
    translateMessage(language, english, message, params), [language]);
  return { language, preference, setPreference, t };
}
